/**
 * Spotlight action catalog: create / navigate / command entries for GlobalSearch.
 * Icons are resolved in UI — keep this module data-only for reuse + tests.
 *
 * Create entries that map in `resolveCreateHostAction` open via CreateDialogHost
 * (no route jump). `path` / `state` remain for recent history + legacy deep links.
 */

import type { NavKey } from '@/shared/layout/nav/types';

export type SpotlightActionKind = 'create' | 'navigate' | 'command';

export type SpotlightActionGroup = 'create' | 'go' | 'command';

export type SpotlightIconId =
  | 'appointment'
  | 'patient'
  | 'lead'
  | 'task'
  | 'project'
  | 'expense'
  | 'dashboard'
  | 'payment'
  | 'staff'
  | 'report'
  | 'flash';

/** Keys under `common` i18n for Spotlight labels. */
export type SpotlightLabelKey =
  | 'spotlightCreateAppointment'
  | 'spotlightCreatePatient'
  | 'spotlightCreateLead'
  | 'spotlightCreateTask'
  | 'spotlightCreateProject'
  | 'spotlightCreateExpense'
  | 'spotlightCreateAdvanceRequest'
  | 'spotlightGoDashboard'
  | 'spotlightGoPatients'
  | 'spotlightGoLeads'
  | 'spotlightGoAppointments'
  | 'spotlightGoTasks'
  | 'spotlightGoProjects'
  | 'spotlightGoKpi'
  | 'spotlightGoPayments'
  | 'spotlightGoExpenses'
  | 'spotlightGoStaff'
  | 'spotlightGoReports'
  | 'spotlightCmdAppointmentsToday'
  | 'spotlightCmdAppointmentsThisWeek'
  | 'spotlightCmdMyTasks'
  | 'spotlightCmdOverdueTasks'
  | 'spotlightCmdTreatmentPlans'
  | 'spotlightCtxCreateAppointment'
  | 'spotlightCtxCreateTask';

export type SpotlightActionDef = {
  id: string;
  kind: SpotlightActionKind;
  group?: SpotlightActionGroup;
  /** Label in `common` (static/create/command). */
  labelKey?: SpotlightLabelKey;
  /** Label in `navigation` (nav index). */
  navLabelKey?: NavKey;
  /** Matching aliases (Vietnamese + English), diacritics optional. */
  keywords: string[];
  path: string;
  /** History state when the destination opens a dialog off the URL. */
  state?: object;
  icon: SpotlightIconId;
  /** Require this permission (admin bypasses via usePermission). */
  permission?: string;
  /** Require any of these permissions (nav items). */
  permissionsAny?: string[];
  /** Hide from empty-query suggestions (bulk nav index). */
  hideWhenEmpty?: boolean;
};

/** Spotlight create destinations (⌘K / search). */
export const SPOTLIGHT_CREATE_ACTIONS: SpotlightActionDef[] = [
  {
    id: 'create-appointment',
    kind: 'create',
    group: 'create',
    labelKey: 'spotlightCreateAppointment',
    keywords: [
      'tao lich',
      'tao lich hen',
      'new appointment',
      'create appointment',
      'lich hen',
      'dat lich',
    ],
    path: '/appointments?view=calendar',
    state: { appointmentCreate: {} },
    icon: 'appointment',
    permission: 'appointments:create',
  },
  {
    id: 'create-patient',
    kind: 'create',
    group: 'create',
    labelKey: 'spotlightCreatePatient',
    keywords: [
      'tao benh nhan',
      'tao bn',
      'new patient',
      'create patient',
      'benh nhan',
      'them bn',
    ],
    path: '/patients',
    state: { openAddPatient: true },
    icon: 'patient',
    permission: 'patients:create',
  },
  {
    id: 'create-lead',
    kind: 'create',
    group: 'create',
    labelKey: 'spotlightCreateLead',
    keywords: ['tao lead', 'new lead', 'create lead', 'lead', 'them lead'],
    path: '/leads',
    state: { openAddLead: true },
    icon: 'lead',
    permission: 'leads:create',
  },
  {
    id: 'create-task',
    kind: 'create',
    group: 'create',
    labelKey: 'spotlightCreateTask',
    keywords: [
      'tao task',
      'tao viec',
      'new task',
      'create task',
      'task',
      'cong viec',
    ],
    path: '/tasks?view=list&mine=1&create=1',
    icon: 'task',
    permission: 'task:create',
  },
  {
    id: 'create-project',
    kind: 'create',
    group: 'create',
    labelKey: 'spotlightCreateProject',
    keywords: [
      'tao du an',
      'tao project',
      'new project',
      'create project',
      'project',
      'du an',
    ],
    path: '/projects?view=list&create=1',
    icon: 'project',
    permission: 'project:create',
  },
  {
    id: 'create-expense',
    kind: 'create',
    group: 'create',
    labelKey: 'spotlightCreateExpense',
    keywords: [
      'tao chi phi',
      'tao phieu chi',
      'new expense',
      'create expense',
      'chi phi',
    ],
    path: '/expenses?create=1',
    icon: 'expense',
    permission: 'expense:create',
  },
  {
    id: 'create-advance-request',
    kind: 'create',
    group: 'create',
    labelKey: 'spotlightCreateAdvanceRequest',
    keywords: [
      'xin tam ung',
      'tam ung luong',
      'request advance',
      'payroll advance',
      'xin ung luong',
    ],
    path: '/profile?tab=advances',
    icon: 'flash',
    permission: 'payroll:advance-request',
  },
];

/** Curated navigate shortcuts (shown in empty suggestions). */
export const SPOTLIGHT_GO_ACTIONS: SpotlightActionDef[] = [
  {
    id: 'go-dashboard',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoDashboard',
    keywords: ['dashboard', 'tong quan', 'home', 'trang chu'],
    path: '/dashboard',
    icon: 'dashboard',
  },
  {
    id: 'go-patients',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoPatients',
    keywords: ['danh sach benh nhan', 'patients', 'benh nhan', 'bn'],
    path: '/patients',
    icon: 'patient',
    permission: 'patients:read',
  },
  {
    id: 'go-leads',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoLeads',
    keywords: ['danh sach lead', 'leads', 'lead', 'lead moi'],
    path: '/leads',
    icon: 'lead',
    permission: 'leads:read',
  },
  {
    id: 'go-appointments',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoAppointments',
    keywords: ['lich hen', 'appointments', 'calendar', 'lich'],
    path: '/appointments?view=calendar',
    icon: 'appointment',
    permission: 'appointments:read',
  },
  {
    id: 'go-tasks',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoTasks',
    keywords: ['tasks', 'viec', 'cong viec', 'danh sach task'],
    path: '/tasks?view=list&mine=0',
    icon: 'task',
    permission: 'task:read',
  },
  {
    id: 'go-projects',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoProjects',
    keywords: ['projects', 'du an', 'danh sach du an', 'project'],
    path: '/projects',
    icon: 'project',
    permission: 'project:read',
  },
  {
    id: 'go-kpi',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoKpi',
    keywords: ['kpi', 'chi tieu', 'hieu suat', 'phieu kpi'],
    path: '/kpi/cycles?tab=sheets',
    icon: 'report',
    permission: 'kpi:read',
  },
  {
    id: 'go-payments',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoPayments',
    keywords: ['thanh toan', 'payments', 'payment'],
    path: '/payments',
    icon: 'payment',
    permission: 'payment:read',
  },
  {
    id: 'go-expenses',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoExpenses',
    keywords: ['chi phi', 'expenses', 'phieu chi'],
    path: '/expenses',
    icon: 'expense',
    permission: 'expense:read',
  },
  {
    id: 'go-staff',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoStaff',
    keywords: ['nhan su', 'staff'],
    path: '/staff',
    icon: 'staff',
    permission: 'staff:read',
  },
  {
    id: 'go-reports',
    kind: 'navigate',
    group: 'go',
    labelKey: 'spotlightGoReports',
    keywords: ['bao cao', 'reports', 'report'],
    path: '/reports/revenue',
    icon: 'report',
    permission: 'reports:read',
  },
];

/** Fixed commands (navigate-only, no LLM). */
export const SPOTLIGHT_COMMAND_ACTIONS: SpotlightActionDef[] = [
  {
    id: 'cmd-appointments-today',
    kind: 'command',
    group: 'command',
    labelKey: 'spotlightCmdAppointmentsToday',
    keywords: [
      'lich hom nay',
      'lich hen hom nay',
      'today',
      'hom nay',
      'appointments today',
      'calendar today',
    ],
    path: '/appointments?view=calendar',
    icon: 'flash',
    permission: 'appointments:read',
  },
  {
    id: 'cmd-appointments-week',
    kind: 'command',
    group: 'command',
    labelKey: 'spotlightCmdAppointmentsThisWeek',
    keywords: [
      'lich tuan nay',
      'lich hen tuan nay',
      'this week',
      'tuan nay',
      'appointments week',
    ],
    path: '/appointments?view=calendar',
    icon: 'flash',
    permission: 'appointments:read',
  },
  {
    id: 'cmd-my-tasks',
    kind: 'command',
    group: 'command',
    labelKey: 'spotlightCmdMyTasks',
    keywords: ['task cua toi', 'viec cua toi', 'my tasks', 'mine', 'cua toi'],
    path: '/tasks?view=list&mine=1',
    icon: 'flash',
    permission: 'task:read',
  },
  {
    id: 'cmd-overdue-tasks',
    kind: 'command',
    group: 'command',
    labelKey: 'spotlightCmdOverdueTasks',
    keywords: [
      'task qua han',
      'viec qua han',
      'overdue',
      'qua han',
      'overdue tasks',
    ],
    path: '/tasks?view=list&mine=1&overdue=1',
    icon: 'flash',
    permission: 'task:read',
  },
  {
    id: 'cmd-treatment-plans',
    kind: 'command',
    group: 'command',
    labelKey: 'spotlightCmdTreatmentPlans',
    keywords: [
      'ke hoach dieu tri',
      'treatment plans',
      'treatment plan',
      'dieu tri',
    ],
    path: '/treatment-plans',
    icon: 'flash',
    permission: 'treatment-plan:read',
  },
];

/** @deprecated Use SPOTLIGHT_GO_ACTIONS + SPOTLIGHT_COMMAND_ACTIONS */
export const SPOTLIGHT_NAV_ACTIONS: SpotlightActionDef[] = [
  ...SPOTLIGHT_GO_ACTIONS,
  ...SPOTLIGHT_COMMAND_ACTIONS,
];

export const SPOTLIGHT_STATIC_ACTIONS: SpotlightActionDef[] = [
  ...SPOTLIGHT_CREATE_ACTIONS,
  ...SPOTLIGHT_GO_ACTIONS,
  ...SPOTLIGHT_COMMAND_ACTIONS,
];

export const SPOTLIGHT_ALL_ACTIONS: SpotlightActionDef[] = SPOTLIGHT_STATIC_ACTIONS;

/** Max actions shown when query is empty (suggested). */
export const SPOTLIGHT_EMPTY_ACTION_LIMIT = 8;
